import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Plus, Search, Trash2, Edit, Calendar, Tag, FolderPlus, ChevronDown, ChevronRight, Copy, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Available template variables
const TEMPLATE_VARIABLES = [
  { category: 'Client', variables: [
    { name: 'client_name', description: 'Client\'s full name' },
    { name: 'client_email', description: 'Client\'s email address' },
    { name: 'client_phone', description: 'Client\'s phone number' }
  ]},
  { category: 'Company', variables: [
    { name: 'company_name', description: 'Your company name' },
    { name: 'company_email', description: 'Your company email' },
    { name: 'company_phone', description: 'Your company phone' }
  ]},
  { category: 'Event', variables: [
    { name: 'event_date', description: 'Event date' },
    { name: 'event_time', description: 'Event start time' },
    { name: 'event_type', description: 'Type of event (Wedding, Corporate, etc.)' },
    { name: 'venue_name', description: 'Venue name' },
    { name: 'venue_address', description: 'Venue address' }
  ]},
  { category: 'Financial', variables: [
    { name: 'total_amount', description: 'Total booking amount' },
    { name: 'paid_amount', description: 'Amount already paid' },
    { name: 'balance_due', description: 'Remaining balance' },
    { name: 'retainer_amount', description: 'Required retainer/deposit amount' },
    { name: 'retainer_due_date', description: 'When retainer is due' },
    { name: 'pay_in_full_offer', description: 'Pay in full discount offer text' }
  ]},
  { category: 'Payment Schedule', variables: [
    { name: 'payment_schedule', description: 'Text list of all payments' },
    { name: 'payment_schedule_table', description: 'HTML table of payments' },
    { name: 'payment_schedule_styled', description: 'Styled payment options card' }
  ]},
  { category: 'Package & Discounts', variables: [
    { name: 'package_name', description: 'Selected package name' },
    { name: 'package_price', description: 'Package price' },
    { name: 'discounts', description: 'Applied discounts' },
    { name: 'discount_tags', description: 'Discount qualification tags' }
  ]}
];

export default function EmailTemplatesPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showVariablesPanel, setShowVariablesPanel] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [expandedCategories, setExpandedCategories] = useState({});
  
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    body: '',
    category_id: ''
  });
  
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    color: '#6366f1'
  });

  useEffect(() => {
    fetchData();
  }, [searchQuery, filterCategory]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filterCategory && filterCategory !== 'all') params.append('category_id', filterCategory);
      
      const [templatesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/email-templates?${params}`),
        axios.get(`${API}/email-categories`)
      ]);
      setTemplates(templatesRes.data);
      setCategories(categoriesRes.data);
      
      // Auto-expand all categories
      const expanded = { uncategorized: true };
      categoriesRes.data.forEach(cat => { expanded[cat.id] = true; });
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSubmit = async () => {
    if (!templateForm.name.trim() || !templateForm.subject.trim() || !templateForm.body.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingTemplate) {
        await axios.put(`${API}/email-templates/${editingTemplate.id}`, templateForm);
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/email-templates`, templateForm);
        toast.success('Template created');
      }
      setShowTemplateDialog(false);
      resetTemplateForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleCategorySubmit = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    try {
      if (editingCategory) {
        await axios.put(`${API}/email-categories/${editingCategory.id}`, categoryForm);
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/email-categories`, categoryForm);
        toast.success('Category created');
      }
      setShowCategoryDialog(false);
      resetCategoryForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      body: template.body,
      category_id: template.category_id || ''
    });
    setShowTemplateDialog(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      color: category.color || '#6366f1'
    });
    setShowCategoryDialog(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API}/email-templates/${templateId}`);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? Templates will be moved to Uncategorized.')) return;
    
    try {
      await axios.delete(`${API}/email-categories/${categoryId}`);
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      await axios.post(`${API}/email-templates`, {
        name: `${template.name} (Copy)`,
        subject: template.subject,
        body: template.body,
        category_id: template.category_id
      });
      toast.success('Template duplicated');
      fetchData();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  const insertVariable = (variable) => {
    const tag = `{{${variable}}}`;
    setTemplateForm(prev => ({
      ...prev,
      body: prev.body + tag
    }));
    toast.success(`Inserted ${tag}`);
  };

  const resetTemplateForm = () => {
    setTemplateForm({ name: '', subject: '', body: '', category_id: '' });
    setEditingTemplate(null);
  };

  const resetCategoryForm = () => {
    setCategoryForm({ name: '', color: '#6366f1' });
    setEditingCategory(null);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Group templates by category
  const templatesByCategory = () => {
    const grouped = { uncategorized: { category: null, templates: [] } };
    
    categories.forEach(cat => {
      grouped[cat.id] = { category: cat, templates: [] };
    });
    
    templates.forEach(template => {
      const catId = template.category_id || 'uncategorized';
      if (grouped[catId]) {
        grouped[catId].templates.push(template);
      } else {
        grouped.uncategorized.templates.push(template);
      }
    });
    
    return Object.entries(grouped).filter(([_, g]) => g.templates.length > 0);
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
      <div className="flex-1 overflow-auto">
        <div className="p-6 max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
                <Mail className="w-8 h-8 text-neon-blue" />
                Email Templates
              </h1>
              <p className="text-muted-foreground mt-1">Create and manage email templates for client communications</p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => { resetCategoryForm(); setShowCategoryDialog(true); }}
                data-testid="add-category-btn"
              >
                <FolderPlus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
              <Button 
                onClick={() => { resetTemplateForm(); setShowTemplateDialog(true); }} 
                className="bg-neon-blue text-black hover:bg-neon-blue/80"
                data-testid="add-template-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Template
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search templates..."
                className="pl-10 bg-muted/30"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-48 bg-muted/30">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                      {cat.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Categories List */}
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {categories.map(cat => (
                <div 
                  key={cat.id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm bg-muted/30 border border-border hover:border-neon-blue/50 transition-colors group"
                >
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                  <span>{cat.name}</span>
                  <button
                    onClick={() => handleEditCategory(cat)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                  >
                    <Edit className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(cat.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Templates List */}
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Loading...</div>
          ) : templates.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="py-12 text-center">
                <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No email templates found</p>
                <Button 
                  onClick={() => { resetTemplateForm(); setShowTemplateDialog(true); }}
                  className="mt-4 bg-neon-blue text-black hover:bg-neon-blue/80"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {templatesByCategory().map(([catId, group]) => (
                <div key={catId}>
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(catId)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors mb-2"
                  >
                    {expandedCategories[catId] ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}
                    {group.category ? (
                      <>
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: group.category.color }} />
                        <span className="font-medium">{group.category.name}</span>
                      </>
                    ) : (
                      <span className="font-medium text-muted-foreground">Uncategorized</span>
                    )}
                    <span className="text-xs text-muted-foreground ml-auto">
                      {group.templates.length} template{group.templates.length !== 1 ? 's' : ''}
                    </span>
                  </button>

                  {/* Templates in Category */}
                  {expandedCategories[catId] && (
                    <div className="space-y-2 pl-4 mb-4">
                      {group.templates.map(template => (
                        <Card key={template.id} className="bg-card border-border hover:border-neon-blue/30 transition-colors">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className="font-semibold text-lg">{template.name}</h3>
                                <p className="text-sm text-muted-foreground mt-1">
                                  <span className="font-medium">Subject:</span> {template.subject}
                                </p>
                                <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                                  {template.body}
                                </p>
                                <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {formatDate(template.created_at)}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 ml-4">
                                <Button variant="ghost" size="sm" onClick={() => handleDuplicateTemplate(template)} title="Duplicate">
                                  <Copy className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleEditTemplate(template)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(template.id)} className="text-red-400 hover:text-red-300">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Template Dialog */}
          <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
            <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-neon-blue" />
                  {editingTemplate ? 'Edit Template' : 'Create Email Template'}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-3 gap-4 py-4">
                <div className="col-span-2 space-y-4">
                  <div className="space-y-2">
                    <Label>Template Name *</Label>
                    <Input
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                      placeholder="e.g., Booking Confirmation"
                      className="bg-muted/30"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select 
                      value={templateForm.category_id || "none"} 
                      onValueChange={(v) => setTemplateForm({ ...templateForm, category_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger className="bg-muted/30">
                        <SelectValue placeholder="Select category (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Category</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                              {cat.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Email Subject *</Label>
                    <Input
                      value={templateForm.subject}
                      onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                      placeholder="e.g., Your Booking Confirmation - {{event_date}}"
                      className="bg-muted/30"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email Body *</Label>
                    <textarea
                      value={templateForm.body}
                      onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                      placeholder="Write your email content here. Use {{variable_name}} to insert dynamic content."
                      className="w-full h-64 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-neon-blue/50 resize-none font-mono text-sm"
                    />
                  </div>
                </div>
                
                {/* Variables Panel */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Template Variables</Label>
                    <Info className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Click a variable to insert it into the email body.
                  </p>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                    {TEMPLATE_VARIABLES.map(group => (
                      <div key={group.category}>
                        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                          {group.category}
                        </h4>
                        <div className="space-y-1">
                          {group.variables.map(v => (
                            <button
                              key={v.name}
                              type="button"
                              onClick={() => insertVariable(v.name)}
                              className="w-full text-left px-2 py-1.5 rounded text-xs hover:bg-muted/50 transition-colors group"
                            >
                              <code className="text-neon-blue">{`{{${v.name}}}`}</code>
                              <p className="text-muted-foreground text-[10px] mt-0.5 group-hover:text-foreground">
                                {v.description}
                              </p>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
                <Button onClick={handleTemplateSubmit} className="bg-neon-blue text-black hover:bg-neon-blue/80">
                  {editingTemplate ? 'Update' : 'Create'} Template
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Category Dialog */}
          <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FolderPlus className="w-5 h-5 text-neon-purple" />
                  {editingCategory ? 'Edit Category' : 'Add Category'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Category Name *</Label>
                  <Input
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    placeholder="e.g., Booking Emails"
                    className="bg-muted/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Color</Label>
                  <div className="flex gap-2">
                    {['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316'].map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setCategoryForm({ ...categoryForm, color })}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          categoryForm.color === color ? 'border-white scale-110' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
                <Button onClick={handleCategorySubmit} className="bg-neon-purple text-white hover:bg-neon-purple/80">
                  {editingCategory ? 'Update' : 'Create'} Category
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
